import React, { useRef } from "react";
import { Map } from "immutable";
import { useDrop } from "react-dnd";

import LayerPanelElement from "./LayerPanelElement";
import { generateDropSpec } from "../../../utils/DnDUtils";
import { pagePanelDrop } from "../SitePanelDropHandlers";

import AppConstants from "@/constants/AppConstants";
import { BOTTOM, ELEMENT_LAYER_ITEM } from "@/constants/DnDConstants";

import styles from "./LayerPanelGroup.module.css";
import layerStyles from "./LayerPanelElement.module.css";

const { ITEM_TYPE_ELEMENT } = AppConstants;

const BottomDropArea = ({ elemKey, index, locked }) => {
  const selfRef = useRef();

  const drop = generateDropSpec({
    type: ELEMENT_LAYER_ITEM,
    canDrop: () => !locked,
    onDrop: pagePanelDrop({
      areaOverride: BOTTOM,
      dropRef: selfRef.current,
      id: elemKey,
      index: index,
      layerType: AppConstants.ITEM_TYPE_GROUP,
    }),
  });

  const [{ isOver, canDrop }, dropRef] = useDrop(drop);

  const useShadow = isOver && canDrop;
  const style = {};

  if (useShadow) {
    // todo: convert these constants to classes
    style.boxShadow = AppConstants.DROP_SHADOW_BOTTOM;
  }

  return (
    <div
      className={styles["bottom-drop-area"]}
      ref={(ref) => {
        selfRef.current = ref;
        dropRef(ref);
      }}
      style={style}
    ></div>
  );
};

const LayerPanelGroup = ({
  group = Map(),
  groupLayer,
  children,
  elemKey,
  id,
  index,
  isGroupOpen,
  locked,
  onSelect,
  onVisibleChange,
  onLayerMove,
  selected,
  selectedChildren,
  setSelectedChildren,
  selectedCanvasView,
  setSelectedCanvasView,
  nonCanvasViewElementSelected,
  siblingElements,
  showBottomDropArea,
}) => {
  const groupElementIds = group.get("elementOrder");
  const groupElements = group.get("elements");
  const groupId = group.get("id");

  // locks child elements when parent is locked
  const lockPassthrough = group.get("lockEditing") === "1" ? true : undefined;

  return (
    <div
      className={`${styles["layer-group"]} ${lockPassthrough ? styles.locked : ""} ${
        isGroupOpen ? layerStyles.open : layerStyles.closed
      }`}
    >
      {groupLayer}
      {isGroupOpen && (
        <div className={styles["layer-group-elements"]}>
          <div className={styles["divider"]} />
          {groupElementIds.map((groupElementId, i) => (
            <LayerPanelElement
              key={groupElementId}
              element={groupElements.get(groupElementId)}
              elemKey={`${elemKey}.elements.${groupElementId}`}
              id={groupElementId}
              group={group}
              groupId={groupId}
              groupPath={elemKey}
              index={i}
              layerType={ITEM_TYPE_ELEMENT}
              locked={lockPassthrough}
              onSelect={onSelect}
              onVisibleChange={onVisibleChange}
              selected={selected}
              selectedChildren={selectedChildren}
              setSelectedChildren={setSelectedChildren}
              selectedCanvasView={selectedCanvasView}
              setSelectedCanvasView={setSelectedCanvasView}
              nonCanvasViewElementSelected={nonCanvasViewElementSelected}
            />
          ))}
          {showBottomDropArea && (
            <BottomDropArea
              key={`bottom-drop-area-${groupId}`}
              elemKey={elemKey}
              index={index}
              id={id}
              locked={lockPassthrough}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LayerPanelGroup;
